/**
 * Whether the upload dialog's "Run OCR on Documents" box starts checked.
 *
 * On by default and remembered per browser. OCR is what makes a scanned
 * document searchable at all, so the useful default is on — but a reviewer who
 * turns it off for one batch means it for the next batch too, and a dialog that
 * silently re-checks it every time would run an engine they declined.
 *
 * THE ONLY STICKY INGESTION OPTION. `runHead`, `runPerson`, `runTranscription`,
 * `runVoiceMask` and `runPII` live in the redux store alone and reset to false
 * on every page load. That is deliberately left alone here: making them sticky
 * changes what a fresh dialog does for everyone already relying on it starting
 * clear, which is a separate decision from this one.
 *
 * ONLY MANUAL TOGGLES ARE RECORDED. Checking "Run PII Detection" forces OCR on
 * (see the FilePicker), and that is not the reviewer expressing a preference
 * about OCR — persisting it would erase a deliberate "off" as a side effect of
 * asking for something else.
 *
 * Storage access is wrapped: Safari private mode and disabled-storage policies
 * throw on read AND on write. A remembered checkbox is not worth a dialog that
 * cannot open, so both fall back to the default and carry on.
 */

import { isNil } from 'lodash';

const STORAGE_KEY = 'redact.upload.runOCR';

/** What the box reads when nothing has been remembered yet. */
export const RUN_OCR_DEFAULT = true;

export function readRunOcrPreference(): boolean {
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    // Absent, not "false" — the two are different answers, and only a stored
    // value may override the default.
    if (isNil(stored)) {
      return RUN_OCR_DEFAULT;
    }
    return stored === 'true';
  } catch {
    return RUN_OCR_DEFAULT;
  }
}

export function writeRunOcrPreference(value: boolean): void {
  try {
    window.localStorage?.setItem(STORAGE_KEY, String(value));
  } catch {
    // Nothing to do and nothing to say: the option still applies to this
    // upload, it just will not be remembered for the next one.
  }
}
